import { type SetStateAction, type Dispatch, useMemo, useState } from 'react';
import { X, Search } from 'lucide-react';
import { usePlatformProducts } from '~/api/platformProducts.tsx';
import {
  useGetPlatformProductsLengthsForProduct,
  useUpsertInventory,
} from '~/api/inventory.tsx';
import { Button } from '~/components/ui';
import { useUpsertBuyerInventoryConfiguration } from '~/api/buyerInventoryConfiguration.tsx';
import { useGetLocations } from '~/api/location.tsx';
import { useTranslation } from 'react-i18next';
import { isNonNegativeNumber } from '~/utils/inventory.tsx';

type AddInventoryModalProps = {
  readonly setShowAddInventory: Dispatch<SetStateAction<boolean>>;
};

function PlatformProductLengths({
  productId,
  onSelect,
}: Readonly<{
  productId: string;
  onSelect: (platformProductsLengthId: string) => void;
}>) {
  const {
    data: lengthsData,
    isLoading,
    error,
  } = useGetPlatformProductsLengthsForProduct(productId);

  const lengths = Array.isArray(lengthsData?.platformProductsLengthsForProduct)
    ? lengthsData.platformProductsLengthsForProduct
    : [];

  const selectPlaceholder = (() => {
    if (isLoading) return 'Loading lengths...';
    if (error) return 'Failed to load lengths';
    if (lengths.length === 0) return 'No lengths available';
    return 'Select a length';
  })();

  return (
    <div>
      <label
        htmlFor="length-select"
        className="block text sm font-medium text-neut-700 mb-2"
      >
        Length <span className="text-error-500">*</span>
      </label>
      <select
        id="length-select"
        className="w-full px-4 py-2 border border-neut-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prim bg-neut-0"
        disabled={isLoading || !!error || lengths.length === 0}
        onChange={(e) => onSelect(e.target.value)}
      >
        <option value="">{selectPlaceholder}</option>
        {lengths.map((l: any) => (
          <option key={l.id} value={l.id}>
            {l.length?.displayValue ?? l.displayValue}
          </option>
        ))}
      </select>
    </div>
  );
}

export function AddInventoryModal({
  setShowAddInventory,
}: AddInventoryModalProps) {
  const { data, isLoading, error } = usePlatformProducts();
  const {
    data: locationsData,
    isLoading: locationsLoading,
    error: locationsError,
  } = useGetLocations();
  const { mutate: mutateInventory } = useUpsertInventory();
  const { mutate: mutateBuyerInventoryConfiguration } =
    useUpsertBuyerInventoryConfiguration();
  const [search, setSearch] = useState('');
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [selectedProductLengthId, setSelectedProductLengthId] =
    useState<string>('');
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [currentStock, setCurrentStock] = useState<number | ''>('');
  const [avgWeeklyConsumption, setAvgWeeklyConsumption] = useState<number | ''>(
    ''
  );
  const [openList, setOpenList] = useState(false);

  const products = Array.isArray(data?.platformProducts)
    ? data.platformProducts
    : [];
  const locations = Array.isArray(locationsData?.locations)
    ? locationsData.locations
    : [];

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p: any) =>
      `${p?.name ?? ''}`.toLowerCase().includes(q)
    );
  }, [products, search]);

  const handleChoose = (p: any) => {
    setSelectedProductId(p.id);
    setSearch(p.name ?? '');
    setOpenList(false);
    setSelectedProductLengthId('');
  };

  const hasValidProductId =
    selectedProductId.trim().length > 0 &&
    selectedProductId !== 'null' &&
    selectedProductId !== 'undefined';

  const canSubmit =
    hasValidProductId &&
    !!selectedProductLengthId &&
    !!selectedLocationId &&
    isNonNegativeNumber(currentStock) &&
    isNonNegativeNumber(avgWeeklyConsumption);

  const onAddInventory = () => {
    if (!canSubmit) return;

    mutateInventory(
      {
        platformProductsLengthId: selectedProductLengthId,
        locationId: selectedLocationId,
        quantity: { value: Number(currentStock), unit: 'MBF' },
      },
      {
        onSuccess: () => {
          mutateBuyerInventoryConfiguration(
            {
              platformProductsLengthId: selectedProductLengthId,
              locationId: selectedLocationId,
              usageRatePerWeek: {
                value: Number(avgWeeklyConsumption),
                unit: 'MBF',
              },
            },
            {
              onSuccess: () => setShowAddInventory(false),
            }
          );
        },
      }
    );
  };

  const { t } = useTranslation('inventory');
  return (
    <div className="bg-neut-0 rounded-lg shadow-xl max-w-xl w-full">
      <div className="px-6 py-4 border-b bg-neut-200 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-neut-prim">
            {t('inventory.add')}
          </h2>
          <p className="text-sm text-neut-500">Create a new inventory item</p>
        </div>
        <button
          className="p-2 hover:bg-neut-100 rounded-lg transition-colors"
          aria-label="Close"
          onClick={() => setShowAddInventory(false)}
        >
          <X
            size={20}
            strokeWidth={2}
            className="lucide lucide-x"
            aria-hidden="true"
          />
        </button>
      </div>

      <div className="p-6 space-y-4">
        <div className="relative">
          <label
            htmlFor="product-search"
            className="block text-sm font-medium text-neut-700 mb-2"
          >
            {t('inventory.product_name')}{' '}
            <span className="text-error-500">*</span>
          </label>

          <div className="relative">
            <Search
              size={16}
              strokeWidth={2}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-neut-400 pointer-events-none z-10"
              aria-hidden="true"
            />
            <input
              id="product-search"
              type="text"
              placeholder={t('inventory.select_product_placeholder')}
              className="w-full pl-10 pr-4 py-2 border border-neut-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prim text-sm"
              value={search}
              onFocus={() => setOpenList(true)}
              onBlur={() => setTimeout(() => setOpenList(false), 100)}
              onChange={(e) => {
                setSearch(e.target.value);
                setOpenList(true);
              }}
            />
          </div>

          {!isLoading && !error && openList && (
            <div className="mt-2 border border-neut-200 rounded-lg max-h-40 overflow-auto bg-neut-0">
              {filteredProducts.length === 0 ? (
                <div className="px-3 py-2 text-sm text-neut-500">
                  {t('inventory.no_products')}
                </div>
              ) : (
                filteredProducts.map((p: any) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-neut-50 ${selectedProductId === p.id ? 'bg-neut-50' : ''}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleChoose(p)}
                    title={p.name}
                  >
                    <span className="font-medium text-neut-800">{p.name}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {!hasValidProductId ? (
          <div>
            <label
              htmlFor="length-select-disabled"
              className="block text-sm font-medium text-neut-700 mb-2"
            >
              {t('inventory.length')} <span className="text-error-500">*</span>
            </label>
            <select
              id="length-select-disabled"
              className="w-full px-4 py-2 border border-neut-300 rounded-lg bg-neut-0"
              disabled
            >
              <option value="">{t('inventory.select_product')}</option>
            </select>
          </div>
        ) : (
          <PlatformProductLengths
            productId={selectedProductId}
            onSelect={setSelectedProductLengthId}
          />
        )}

        <div>
          <label
            htmlFor="location-select"
            className="block text-sm font-medium text-neut-700 mb-2"
          >
            {t('inventory.location')} <span className="text-error-500">*</span>
          </label>
          <select
            id="location-select"
            className="w-full px-4 py-2 border border-neut-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prim bg-neut-0"
            disabled={
              locationsLoading || !!locationsError || locations.length === 0
            }
            onChange={(e) => setSelectedLocationId(e.target.value)}
          >
            {(() => {
              let placeholder = t('inventory.select_location');
              if (locationsLoading) {
                placeholder = t('inventory.locations_loading');
              } else if (locationsError) {
                placeholder = t('inventory.locations_failed');
              } else if (locations.length === 0) {
                placeholder = t('inventory.locations_empty');
              }
              return <option value="">{placeholder}</option>;
            })()}
            {locations.map((loc: any) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="current-stock"
            className="block text-sm font-medium text-neut-700 mb-2"
          >
            {t('inventory.stock')} (MBF){' '}
            <span className="text-error-500">*</span>
          </label>
          <input
            id="current-stock"
            type="number"
            min={0}
            className="w-full px-4 py-2 border border-neut-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prim"
            placeholder={t('inventory.stock_placeholder')}
            value={currentStock}
            onChange={(e) => {
              const v = e.target.value;
              setCurrentStock(v === '' ? '' : Number(v));
            }}
          />
        </div>

        <div>
          <label
            htmlFor="avg-weekly"
            className="block text-sm font-medium text-neut-700 mb-2"
          >
            {t('inventory.average_consumption')} (MBF/week){' '}
            <span className="text-error-500">*</span>
          </label>
          <input
            id="avg-weekly"
            type="number"
            min={0}
            className="w-full px-4 py-2 border border-neut-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prim"
            placeholder={t('inventory.average_consumption_placeholder')}
            value={avgWeeklyConsumption}
            onChange={(e) => {
              const v = e.target.value;
              setAvgWeeklyConsumption(v === '' ? '' : Number(v));
            }}
          />
          <p className="text-xs text-neut-500 mt-1">
            {t('inventory.average_consumption_subscript')}
          </p>
        </div>
      </div>

      <div className="px-6 py-4 border-t border-neut-200 flex items-center justify-end gap-3">
        <Button
          variant={'secondary'}
          onClick={() => setShowAddInventory(false)}
        >
          {t('inventory.cancel')}
        </Button>
        <Button disabled={!canSubmit} onClick={onAddInventory}>
          {t('inventory.add')}
        </Button>
      </div>
    </div>
  );
}
